<template>
    <div class="flex justify-end" :class="{ 'flex-col': view === 'year' }">
        <h4 v-if="view === 'year'" class="text-center font-bold text-lg mb-3">
            {{ monthLabel }}
        </h4>
        <div
            class="flex flex-auto flex-col mx-auto"
            :class="{
                'w-[346px]': size === 'mini',
            }"
        >
            <Weekdays :size="size" />
            <div
                class="flex text-xs leading-6 text-gray-700 lg:flex-auto overflow-hidden"
                :class="classes"
            >
                <div class="w-full grid grid-cols-7" :class="classes">
                    <MonthDayItem
                        v-for="day in days"
                        :size="size"
                        :key="day.date"
                        :day="day"
                        :disable-past-dates="true"
                        class="border-slate-200"
                    />
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import { mapState, mapActions } from 'pinia';
import { useCalendarStore } from '@/store/calendar';
import dayjs from 'dayjs';
import Weekdays from '@/components/calendar/partials/weekdays.vue';
import MonthDayItem from '@/components/calendar/partials/month-day-item.vue';

export default {
    name: 'CalendarMonth',
    props: {
        size: {
            type: String,
            default: 'full', // full | mini
        },
        monthDate: {
            type: String,
            default: null,
        },
        hidePastDates: {
            type: Boolean,
            default: false,
        },
        showFullWeeks: {
            type: Boolean,
            default: true,
        },
    },
    components: {
        Weekdays,
        MonthDayItem,
    },
    data: () => ({}),
    computed: {
        ...mapState(useCalendarStore, [
            'events',
            'view',
            'weekdays',
            'activeDate',
            'activeYear',
            'activeMonth',
            'previousMonthDays',
            'currentMonthDays',
            'nextMonthDays',
        ]),
        // bg-gray-200
        classes() {
            return this.size === 'full'
                ? 'rounded-md border-r border-b border-slate-200  overflow-hidden divide-x divide-y divide-slate-200'
                : '';
        },
        localDate() {
            return this.view === 'year'
                ? dayjs(this.monthDate)
                : this.activeDate;
        },
        numberOfDaysInMonth() {
            return this.localDate.daysInMonth();
        },
        currentMonthDays() {
            return [...Array(this.numberOfDaysInMonth)].map((day, index) => {
                let currentDate = dayjs(
                    `${this.localDate.format('YYYY')}-${this.localDate.format(
                        'MM'
                    )}-${index + 1}`
                );
                let dayDate = currentDate.format('YYYY-MM-DD');
                let events = this.events.filter((event) => {
                    return event.start_date === dayDate;
                });
                return {
                    date: dayDate,
                    dayLabel: currentDate.format('D'),
                    isCurrentMonth: true,
                    events: events,
                };
            });
        },
        previousMonthDays() {
            if (!this.showFullWeeks) {
                return [];
            }
            const firstDayOfTheMonthWeekday = this.getWeekday(
                this.currentMonthDays[0].date
            );
            const previousMonth = dayjs(
                `${this.localDate.format('YYYY')}-${this.localDate.format(
                    'MM'
                )}-01`
            ).subtract(1, 'month');
            const visibleNumberOfDaysFromPreviousMonth =
                firstDayOfTheMonthWeekday ? firstDayOfTheMonthWeekday - 1 : 6;

            const previousMonthLastMondayDayOfMonth = dayjs(
                this.currentMonthDays[0].date
            )
                .subtract(visibleNumberOfDaysFromPreviousMonth, 'day')
                .date();

            return [...Array(visibleNumberOfDaysFromPreviousMonth)].map(
                (day, index) => {
                    let prevDate = dayjs(
                        `${previousMonth.year()}-${previousMonth.month() + 1}-${
                            previousMonthLastMondayDayOfMonth + index
                        }`
                    );
                    let dayDate = prevDate.format('YYYY-MM-DD');
                    let events = this.events.filter((event) => {
                        return event.start_date === dayDate;
                    });
                    return {
                        date: dayDate,
                        dayLabel: prevDate.format('D'),
                        isCurrentMonth: false,
                        events: events,
                    };
                }
            );
        },
        nextMonthDays() {
            if (!this.showFullWeeks) {
                return [];
            }
            const lastDayOfTheMonthWeekday = this.getWeekday(
                `${this.localDate.format('YYYY')}-${this.localDate.format(
                    'MM'
                )}-${this.currentMonthDays.length}`
            );
            const nextMonth = dayjs(
                `${this.localDate.format('YYYY')}-${this.localDate.format(
                    'MM'
                )}-01`
            ).add(1, 'month');
            const visibleNumberOfDaysFromNextMonth = lastDayOfTheMonthWeekday
                ? 7 - lastDayOfTheMonthWeekday
                : lastDayOfTheMonthWeekday;

            return [...Array(visibleNumberOfDaysFromNextMonth)].map(
                (day, index) => {
                    let nextDate = dayjs(
                        `${nextMonth.year()}-${nextMonth.month() + 1}-${
                            index + 1
                        }`
                    );
                    let dayDate = nextDate.format('YYYY-MM-DD');
                    let events = this.events.filter((event) => {
                        return event.start_date === dayDate;
                    });
                    return {
                        date: dayDate,
                        dayLabel: nextDate.format('D'),
                        isCurrentMonth: false,
                        events: events,
                    };
                }
            );
        },
        monthLabel() {
            return this.localDate.format('MMMM');
        },
        days() {
            return [
                ...this.previousMonthDays,
                ...this.currentMonthDays,
                ...this.nextMonthDays,
            ];
        },
    },
    methods: {
        ...mapActions(useCalendarStore, ['setMonthDate', 'getWeekday']),
    },
    created() {},
    mounted() {},
};
</script>
